import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, FormGroup, Validators } from "@angular/forms";
import { SubSink } from "subsink";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { DynamicInput } from "../../../../../dynamic-forms/inputs/dynamic-input.model";
import { Switch } from "../../../../../dynamic-forms/inputs/switch/switch.model";
import { ErrorPosition } from "../../../../../dynamic-forms/inputs/textbox/error-position.enum";
import { TextboxType } from "../../../../../dynamic-forms/inputs/textbox/textbox-type.enum";
import { Textbox } from "../../../../../dynamic-forms/inputs/textbox/textbox.model";
import { LookupModel } from "../../../../../shared/models/lookup-model";
import { ArrayHelper } from "../../../../../utilities/contracts/array-helper";
import { StringHelper } from "../../../../../utilities/contracts/string-helper";
import { RegExHelper } from "../../../../../utilities/reg-Ex-Helper";
import { ProductType } from "../../new-project/product-type.enum";
import { IDisabledRateTypes, IOverreadModel, RateType } from "../models/overread.model";
import { IRateModel } from "../models/rates.model";

interface IOverreadForm {
  formGroup: FormGroup;
  models: DynamicInput[];
}

enum ActiveFormInputs {
  ACTIVE = "active",
}

enum STATUS {
  INACTIVE,
  ACTIVE,
}

@Component({
             selector: "app-project-sampling-overread",
             templateUrl: "./project-sampling.component.html",
             styleUrls: ["./project-sampling.component.scss"],
           })
export class ProjectSamplingComponent implements OnInit, OnDestroy {

  @Input() set overreadInfo(info: IOverreadModel) {
    if (!ArrayHelper.isAvailable(info?.overread?.rates?.rate) || !ArrayHelper.isAvailable(info?.overread2?.rates?.rate)) {
      this.patchDefaultValues("OR1");
      this.patchDefaultValues("OR2");
      this.activeFormGroup.formGroup?.patchValue({ [ActiveFormInputs.ACTIVE]: 0 });
      this.emitDisabledRateTypes();
      return;
    }
    const overread1Info = info.overread.rates.rate.filter(r => !StringHelper.isAvailable(r?.measurecode));
    const overread2Info = info.overread2.rates.rate.filter(r => !StringHelper.isAvailable(r?.measurecode));
    this.patchValues(overread1Info, "OR1");
    this.patchValues(overread2Info, "OR2");
    // single switch: active if either stage was active
    const isActive = overread1Info.some(e => e.active === STATUS.ACTIVE) || overread2Info.some(e => e.active === STATUS.ACTIVE);
    this.activeFormGroup.formGroup?.patchValue({ [ActiveFormInputs.ACTIVE]: isActive ? 1 : 0 });
    this.emitDisabledRateTypes();
  }

  @Input() projectLookupDetails: LookupModel;

  @Output() disabledRateTypesChange = new EventEmitter<IDisabledRateTypes>();

  formOverread: IOverreadForm = {formGroup: undefined, models: []};
  formOverread2: IOverreadForm = {formGroup: undefined, models: []};
  activeFormGroup: IOverreadForm = {formGroup: undefined, models: []};

  private sink = new SubSink();

  constructor(private readonly formService: FormService) {
  }

  ngOnInit(): void {
    this.setFormControls();
    const activeSwitch = new Switch(
      {
        key: ActiveFormInputs.ACTIVE,
        label: "",
      });
    this.activeFormGroup.formGroup = this.formService.createFormGroup([activeSwitch]);
    this.activeFormGroup.models = [activeSwitch];

    this.sink.add(
      this.activeFormGroup.formGroup.controls[ActiveFormInputs.ACTIVE].valueChanges
        .subscribe(() => this.setOverreadFormsEnabled())
    );
    this.setOverreadFormsEnabled();

    // overreadInfo can land before the controls exist; re-emit once they do.
    this.emitDisabledRateTypes();
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }

  onOverreadValueChange(): void {
    this.emitDisabledRateTypes();
  }

  get isActive(): boolean {
    return Number(this.activeFormGroup.formGroup?.get(ActiveFormInputs.ACTIVE)?.value) === STATUS.ACTIVE;
  }

  get isHedisNextGen(): boolean {
    return this.projectLookupDetails.templateID === ProductType.FULL_WORKFLOW_V2025_NEWGEN || this.projectLookupDetails.templateID === ProductType.FULL_WORKFLOW_V2026_NEWGEN;
  }

  getControls(form: FormGroup): AbstractControl[] {
    const controls: AbstractControl[] = [];
    for (const [key, control] of Object.entries(form.controls)) {
      controls.push(control);
    }
    return controls;
  }

  trackByIndex(index: number): number {
    return index;
  }

  formHasErrors(): boolean {
    return this.formOverread.formGroup.invalid || this.formOverread2.formGroup.invalid;
  }

  getValuesAsObject(type: "OR1" | "OR2" = "OR1"): IRateModel[] {
    if (type === "OR1") {
      return [...this.getFormattedValues("OR1")];
    }
    return [...this.getFormattedValues("OR2")];
  }

  private emitDisabledRateTypes(): void {
    this.disabledRateTypesChange.emit({
      OR1: this.getZeroRateTypes(this.formOverread.formGroup),
      OR2: this.getZeroRateTypes(this.formOverread2.formGroup),
    });
  }

  private setOverreadFormsEnabled(): void {
    [this.formOverread.formGroup, this.formOverread2.formGroup].forEach(formGroup => {
      if (this.isActive) {
        formGroup?.enable({ emitEvent: false });
      } else {
        formGroup?.disable({ emitEvent: false });
      }
    });
  }

  private getZeroRateTypes(formGroup: FormGroup): RateType[] {
    if (formGroup == null) {
      return [];
    }
    return Object.values(RateType).filter(ratetype => {
      const value = formGroup.get(ratetype)?.value;
      // a cleared number input writes null, and Number(null) === 0 -- guard before comparing
      return value !== null && value !== undefined && value !== "" && Number(value) === 0;
    });
  }

  private getFormattedValues(type: "OR1" | "OR2" = "OR1"): IRateModel[] {
    const active = Number(this.activeFormGroup.formGroup.get(ActiveFormInputs.ACTIVE).value); // single switch drives both stages
    if (type === "OR1") {
      return Object.values(RateType).map(v => {
        return {
          value: this.formOverread.formGroup.get(v)?.value as number,
          ratetype: v,
          active,
        };
      });
    }
    return Object.values(RateType).map(v => {
      return {
        value: this.formOverread2.formGroup.get(v)?.value as number,
        ratetype: v,
        active,
      };
    });
  }

  private setFormControls(): void {
    const models: DynamicInput[] = [];
    Object.values(RateType).forEach((input: string) => {
      models.push(this.createTextbox(input, ""));
    });
    this.formOverread = {
      formGroup: this.formService.createFormGroup([...models]),
      models,
    };
    this.formOverread2 = {
      formGroup: this.formService.createFormGroup([...models]),
      models,
    };
  }

  private createTextbox(key: string, label: string): Textbox {
    return new Textbox(
      {
        key,
        label,
        validators: [Validators.min(0), Validators.max(100), Validators.required, Validators.pattern(RegExHelper.wholeNumber)],
        errorPosition: ErrorPosition.BOTTOM,
        errorMessages: {
          required: "Enter a whole number 0–100",
          min: "Enter 0 - 100.",
          max: "Enter 0 - 100.",
          pattern: "Enter 0 - 100.",
        },
      });
  }

  private patchValues(arr: IRateModel[], type: "OR1" | "OR2" = "OR1"): void {
    if (!ArrayHelper.isAvailable(arr)) {
      this.patchDefaultValues(type);
      return;
    }
    const mapToObjectArray = arr.map(rate => {
      return { [rate.ratetype]: rate.value };
    });
    const patchObject = Object.assign({}, ...mapToObjectArray);
    if (type === "OR1") {
      this.formOverread.formGroup.patchValue(patchObject);
      return;
    }
    this.formOverread2.formGroup.patchValue(patchObject);
  }

  private patchDefaultValues(type: "OR1" | "OR2" = "OR1"): void {
    if (type === "OR1") {
      const patchValuesOR1 = {
        compliant: 0,
        partiallycompliant: 0,
        noncompliant: 0,
        noncompliantexclusion: 0,
      };
      this.formOverread.formGroup?.patchValue(patchValuesOR1);
      return;
    }
    const patchValuesOR2 = {
      compliant: 0,
      partiallycompliant: 0,
      noncompliant: 0,
      noncompliantexclusion: 0,
    };
    this.formOverread2.formGroup?.patchValue(patchValuesOR2);
  }

}
